import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Folder, ChevronRight, Home, ArrowLeft } from "lucide-react";
import { api } from "@/lib/api";

export default function FolderPickerDialog({ open, onOpenChange, onPick }) {
  const [stack, setStack] = useState([]); // [{id, name}] navigation history; last entry = current
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(false);
  const current = stack[stack.length - 1] || null;

  const loadChildren = async (parentId) => {
    setLoading(true);
    try {
      const { data } = await api.get("/drive/folders", { params: parentId ? { parentId } : {} });
      setFolders(data.folders || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to list folders");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      setStack([]);
      loadChildren(null);
    }
  }, [open]);

  const openFolder = (f) => {
    setStack((s) => [...s, { id: f.id, name: f.name }]);
    loadChildren(f.id);
  };

  const goUp = () => {
    setStack((s) => {
      const next = s.slice(0, -1);
      const parent = next[next.length - 1] || null;
      loadChildren(parent ? parent.id : null);
      return next;
    });
  };

  const setAsRoot = () => {
    if (!current) {
      toast.error("Open a folder before setting it as the source folder");
      return;
    }
    onPick(current);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Choose source folder</DialogTitle>
        </DialogHeader>

        <div className="flex items-center gap-1 text-xs text-slate-600 min-h-[24px] flex-wrap">
          <button
            className="inline-flex items-center gap-1 hover:text-slate-900"
            onClick={() => { setStack([]); loadChildren(null); }}
            data-testid="picker-crumb-root"
          >
            <Home className="w-3 h-3" /> My Drive
          </button>
          {stack.map((s, i) => (
            <span key={s.id} className="inline-flex items-center gap-1">
              <ChevronRight className="w-3 h-3 text-slate-400" />
              <button
                className="hover:text-slate-900 truncate max-w-[180px]"
                onClick={() => {
                  const next = stack.slice(0, i + 1);
                  setStack(next);
                  loadChildren(s.id);
                }}
                title={s.name}
              >
                {s.name}
              </button>
            </span>
          ))}
        </div>

        <div className="border border-zinc-200 rounded-md max-h-[50vh] overflow-y-auto" data-testid="picker-list">
          {loading ? (
            <div className="p-4 text-sm text-slate-500">Loading…</div>
          ) : folders.length === 0 ? (
            <div className="p-6 text-sm text-slate-500 text-center">No subfolders here.</div>
          ) : (
            folders.map((f) => (
              <button
                key={f.id}
                onClick={() => openFolder(f)}
                className="w-full text-left flex items-center gap-2 px-3 py-2 text-sm border-b border-zinc-100 last:border-b-0 hover:bg-zinc-50"
                data-testid={`picker-folder-${f.id}`}
              >
                <Folder className="w-4 h-4 text-slate-500" />
                <span className="flex-1 truncate">{f.name}</span>
                <ChevronRight className="w-4 h-4 text-slate-400" />
              </button>
            ))
          )}
        </div>

        <DialogFooter className="flex sm:justify-between gap-2">
          <Button variant="ghost" onClick={goUp} disabled={stack.length === 0} data-testid="picker-up">
            <ArrowLeft className="w-4 h-4" /> Up
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="picker-cancel">Cancel</Button>
            <Button
              className="bg-zinc-900 hover:bg-zinc-800 text-white"
              onClick={setAsRoot}
              disabled={!current}
              data-testid="picker-set-root"
            >
              Set “{current?.name || "…"}” as source
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
